import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  Calendar,
  CheckCircle2,
  DollarSign,
  FileText,
  Info,
  ListOrdered,
  Loader2,
  Trash2,
  Upload,
  User,
  X,
} from "lucide-react";
import Select from "react-select";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import {
  createInvoice,
  getInvoiceDetail,
  updateInvoice,
  INVOICE_TYPE_OPTIONS,
  getStatusOptions,
} from "../../../api/finance/invoices/invoiceRequests";
import InvoiceItemsEditor from "./InvoiceItemsEditor";
import {
  emptyInvoiceItem,
  formItemsToPayload,
  mapApiItemsToForm,
  sumItemsSubtotal,
} from "./invoiceItemsImport";
import {
  showConfirmDialog,
  showErrorToast,
  showSuccessToast,
} from "../../../components/ConfirmDialog";
import { formatDateVN, formatDateTimeVN } from "../../../utils/formatUtils";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  filterCustomerOption,
  formatCustomerOptionLabel,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";

const fieldSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...customSelectStyles.control(provided, state),
    minHeight: "42px",
    height: "42px",
    borderRadius: "12px",
  }),
};

const branchSelectStyles = {
  ...fieldSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const customerSelectStyles = {
  ...customerInModalSelectStyles,
  control: (provided, state) => ({
    ...customerInModalSelectStyles.control(provided, state),
    minHeight: "42px",
    borderRadius: "12px",
  }),
};

const formatCurrency = (value) => Number(value || 0).toLocaleString("vi-VN");

/** Hiển thị số có dấu phần nghìn (vi-VN); value lưu dạng chuỗi số thuần. */
const formatMoneyInput = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const digits = String(value).replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("vi-VN");
};

const parseMoneyInput = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits;
};

/** VAT % — cho phép số thập phân (vd 8, 8.5) */
const parsePercentInput = (raw) => {
  let s = String(raw || "").replace(/[^\d.,]/g, "").replace(",", ".");
  const parts = s.split(".");
  if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join("")}`;
  if (s.startsWith(".")) s = `0${s}`;
  return s;
};

const calcVatAmount = (subtotal, vatPercent) => {
  const sub = Number(subtotal) || 0;
  const rate = Number(vatPercent) || 0;
  if (sub <= 0 || rate < 0) return 0;
  return Math.round(sub * (rate / 100));
};

/** Tổng = Trước VAT × (100% + VAT%) */
const calcTotalFromVatPercent = (subtotal, vatPercent) => {
  const sub = Number(subtotal) || 0;
  const rate = Number(vatPercent) || 0;
  if (sub <= 0) return 0;
  return Math.round(sub * (1 + rate / 100));
};

const deriveVatPercent = (subtotal, vatAmount) => {
  const sub = Number(subtotal) || 0;
  const vat = Number(vatAmount) || 0;
  if (sub <= 0) return "";
  const pct = (vat / sub) * 100;
  // Làm tròn gọn (vd 8, 10, 8.5)
  const rounded = Math.round(pct * 100) / 100;
  return String(rounded);
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatFileDateTime = (timestamp) => formatDateTimeVN(timestamp);

const emptyForm = (invoiceType = "purchase", branchId = "") => {
  const type = invoiceType === "sale" ? "sale" : "purchase";
  return {
    invoice_number: "",
    invoice_type: type,
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    partner_customer_id: "",
    partner_name: "",
    partner_tax_code: "",
    branch_id: branchId || "",
    subtotal: "",
    vat_percent: "",
    total_amount: "",
    processing_status: type === "sale" ? "issued" : "pending",
    description: "",
    note: "",
  };
};

const InvoiceCreateModal = ({
  isOpen,
  onClose,
  invoice,
  onSaved,
  defaultInvoiceType = "purchase",
}) => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userBranchId = user?.branch_id || user?.branch?.id || "";

  const [form, setForm] = useState(() =>
    emptyForm(defaultInvoiceType, userBranchId)
  );
  const [saving, setSaving] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [shouldReset, setShouldReset] = useState(true);
  const [attachedFile, setAttachedFile] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [dragOverAttach, setDragOverAttach] = useState(false);
  const [items, setItems] = useState(() => [emptyInvoiceItem()]);

  const isEdit = !!invoice?.id;
  const isSale = form.invoice_type === "sale";

  const syncSubtotalFromItems = (nextItems) => {
    const sub = sumItemsSubtotal(nextItems);
    setForm((prev) => {
      const subtotal = sub > 0 ? String(sub) : "";
      const total = calcTotalFromVatPercent(subtotal, prev.vat_percent);
      return {
        ...prev,
        subtotal,
        total_amount: total > 0 ? String(total) : "",
      };
    });
  };

  const handleItemsChange = (nextItems) => {
    setItems(nextItems);
    syncSubtotalFromItems(nextItems);
  };

  /** Import XML HĐĐT → điền header + dòng hàng (đối tác: NBan nếu mua, NMua nếu bán). */
  const handleXmlImported = ({ header, items: nextItems }) => {
    setItems(nextItems?.length ? nextItems : [emptyInvoiceItem()]);
    setForm((prev) => {
      const vat_percent =
        header?.vat_percent != null && header.vat_percent !== ""
          ? String(header.vat_percent)
          : prev.vat_percent;
      const subtotalFromItems = sumItemsSubtotal(nextItems || []);
      const subtotal =
        header?.subtotal && Number(header.subtotal) > 0
          ? String(Math.round(Number(header.subtotal)))
          : subtotalFromItems > 0
            ? String(subtotalFromItems)
            : prev.subtotal;
      const totalFromXml =
        header?.total_amount && Number(header.total_amount) > 0
          ? String(Math.round(Number(header.total_amount)))
          : "";
      const total =
        totalFromXml ||
        (calcTotalFromVatPercent(subtotal, vat_percent) > 0
          ? String(calcTotalFromVatPercent(subtotal, vat_percent))
          : prev.total_amount);

      return {
        ...prev,
        invoice_number: header?.invoice_number || prev.invoice_number,
        invoice_date: header?.invoice_date || prev.invoice_date,
        partner_name: header?.partner_name || prev.partner_name,
        partner_tax_code:
          header?.partner_tax_code || prev.partner_tax_code,
        partner_customer_id: "",
        subtotal,
        vat_percent,
        total_amount: total,
        description:
          header?.description && !prev.description?.trim()
            ? header.description
            : prev.description,
      };
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    getAllBranches()
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        setBranchOptions(
          items.map((b) => ({ value: b.id, label: b.name || "—" }))
        );
      })
      .catch(() => setBranchOptions([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && invoice) {
      setForm({
        invoice_number: invoice.invoice_number || "",
        invoice_type: invoice.invoice_type || "purchase",
        invoice_date: invoice.invoice_date
          ? String(invoice.invoice_date).slice(0, 10)
          : emptyForm().invoice_date,
        due_date: invoice.due_date
          ? String(invoice.due_date).slice(0, 10)
          : "",
        partner_customer_id: "",
        partner_name: invoice.partner_name || "",
        partner_tax_code: invoice.partner_tax_code || "",
        branch_id: invoice.branch_id || "",
        subtotal:
          invoice.subtotal != null
            ? String(Math.round(Number(invoice.subtotal)))
            : "",
        vat_percent: deriveVatPercent(invoice.subtotal, invoice.vat_amount),
        total_amount:
          invoice.total_amount != null
            ? String(Math.round(Number(invoice.total_amount)))
            : "",
        processing_status:
          invoice.processing_status ||
          (invoice.invoice_type === "sale" ? "issued" : "pending"),
        description: invoice.description || "",
        note: invoice.note || "",
      });
      setExistingAttachment(
        invoice.attachment_url
          ? {
              url: invoice.attachment_url,
              name: invoice.attachment_name || "file-dinh-kem.pdf",
            }
          : null
      );
      setAttachedFile(null);
      setPreviewName("");
      setRemoveAttachment(false);
      setShouldReset(false);

      const applyItems = (rawItems) => {
        const mapped = mapApiItemsToForm(rawItems);
        setItems(mapped.length ? mapped : [emptyInvoiceItem()]);
      };

      if (Array.isArray(invoice.items) && invoice.items.length) {
        applyItems(invoice.items);
      } else if (invoice.id) {
        getInvoiceDetail(invoice.id)
          .then((detail) => applyItems(detail?.items || []))
          .catch(() => setItems([emptyInvoiceItem()]));
      } else {
        setItems([emptyInvoiceItem()]);
      }
      return;
    }

    if (shouldReset) {
      setForm(emptyForm(defaultInvoiceType, userBranchId));
      setAttachedFile(null);
      setPreviewName("");
      setExistingAttachment(null);
      setRemoveAttachment(false);
      setItems([emptyInvoiceItem()]);
      setShouldReset(false);
    } else if (!isEdit) {
      // Mở tạo mới với loại đã chọn sẵn từ nút HĐ mua / HĐ bán
      const nextType = defaultInvoiceType === "sale" ? "sale" : "purchase";
      setForm((prev) => ({
        ...prev,
        invoice_type: nextType,
        processing_status: nextType === "sale" ? "issued" : "pending",
        partner_customer_id: "",
        partner_name: "",
        partner_tax_code: "",
      }));
      setItems([emptyInvoiceItem()]);
    }
  }, [isOpen, invoice, isEdit, shouldReset, defaultInvoiceType, userBranchId]);

  useEffect(() => {
    if (!isOpen || !isSale || !form.branch_id) {
      setCustomerOptions([]);
      setLoadingCustomers(false);
      return;
    }

    let cancelled = false;
    setLoadingCustomers(true);
    getCustomersByBranch(form.branch_id)
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.items) ? res.items : res || [];
        setCustomerOptions(
          (Array.isArray(items) ? items : []).map((c) => ({
            value: c.id,
            label: c.name,
            ward: c.ward || "",
            branchName: c.branch_name || c.branchName || "",
            taxCode: c.tax_code || c.taxCode || "",
          }))
        );
      })
      .catch(() => {
        if (!cancelled) setCustomerOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustomers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, isSale, form.branch_id]);

  // Khi sửa HĐ bán: khớp option KH theo tên nếu chưa có id
  useEffect(() => {
    if (!isSale || !form.partner_name || form.partner_customer_id) return;
    if (!customerOptions.length) return;
    const matched = customerOptions.find(
      (o) =>
        String(o.label || "").trim().toLowerCase() ===
        String(form.partner_name).trim().toLowerCase()
    );
    if (matched) {
      setForm((prev) => ({
        ...prev,
        partner_customer_id: matched.value,
        partner_tax_code: matched.taxCode || prev.partner_tax_code || "",
      }));
    }
  }, [isSale, form.partner_name, form.partner_customer_id, customerOptions]);

  const title = useMemo(() => {
    if (isEdit) return "Cập nhật hóa đơn";
    return isSale ? "Ghi nhận hóa đơn bán" : "Ghi nhận hóa đơn mua";
  }, [isEdit, isSale]);

  const updateMoneyField = (field, raw) => {
    const value = field === "vat_percent" ? parsePercentInput(raw) : parseMoneyInput(raw);
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "subtotal" || field === "vat_percent") {
        const sub = field === "subtotal" ? value : prev.subtotal;
        const rate = field === "vat_percent" ? value : prev.vat_percent;
        const total = calcTotalFromVatPercent(sub, rate);
        next.total_amount = total > 0 ? String(total) : "";
      }
      return next;
    });
  };

  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleBranchChange = (opt) => {
    setForm((prev) => ({
      ...prev,
      branch_id: opt?.value || "",
      partner_customer_id: "",
      partner_name: isSale ? "" : prev.partner_name,
      partner_tax_code: isSale ? "" : prev.partner_tax_code,
    }));
  };

  const handleCustomerChange = (opt) => {
    setForm((prev) => ({
      ...prev,
      partner_customer_id: opt?.value || "",
      partner_name: opt?.label || "",
      partner_tax_code: opt?.taxCode || "",
    }));
  };

  const selectedType =
    INVOICE_TYPE_OPTIONS.find((x) => x.value === form.invoice_type) || null;
  const selectedBranch =
    branchOptions.find((x) => String(x.value) === String(form.branch_id)) ||
    null;
  const selectedCustomer =
    customerOptions.find(
      (x) => String(x.value) === String(form.partner_customer_id)
    ) || null;

  const completionCount = [
    form.invoice_number,
    form.partner_name,
    form.invoice_date,
    form.total_amount,
  ].filter(Boolean).length;

  const handleCancel = async () => {
    if (saving) return;
    const confirm = await showConfirmDialog({
      title: "Xác nhận hủy",
      text: "Bạn có muốn xóa toàn bộ dữ liệu đang nhập?",
      confirmText: "Xóa",
      cancelText: "Giữ lại",
    });
    if (confirm.isConfirmed) {
      setShouldReset(true);
      onClose?.();
    }
  };

  const handleCloseInstant = () => {
    setShouldReset(false);
    onClose?.();
  };

  const applyAttachmentFile = (file) => {
    if (!file) return;
    const isPdf =
      file.type === "application/pdf" ||
      /\.pdf$/i.test(file.name || "");
    const isImage =
      String(file.type || "").startsWith("image/") ||
      /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    if (!isPdf && !isImage) {
      showErrorToast("Chỉ cho phép PDF hoặc ảnh (JPEG/PNG/WebP).");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorToast("File vượt quá 25MB.");
      return;
    }
    setAttachedFile(file);
    setPreviewName(file.name);
    setRemoveAttachment(false);
    setDragOverAttach(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    applyAttachmentFile(file);
  };

  const handleAttachDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    setDragOverAttach(true);
  };

  const handleAttachDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Chỉ tắt highlight khi rời khỏi vùng drop (không phải child)
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverAttach(false);
  };

  const handleAttachDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverAttach(false);
    const file = e.dataTransfer?.files?.[0];
    applyAttachmentFile(file);
  };

  const clearAttachment = () => {
    setAttachedFile(null);
    setPreviewName("");
    setDragOverAttach(false);
    if (existingAttachment) {
      setRemoveAttachment(true);
      setExistingAttachment(null);
    }
  };

  const buildRequestPayload = (data) => {
    const needsFormData =
      !!attachedFile || removeAttachment || data.items != null;
    if (!needsFormData) return data;

    const fd = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (key === "items") {
        fd.append("items", JSON.stringify(value));
        return;
      }
      fd.append(key, String(value));
    });
    if (attachedFile) fd.append("attachment", attachedFile);
    if (removeAttachment) fd.append("remove_attachment", "true");
    return fd;
  };

  const handleSubmit = async () => {
    if (!form.invoice_number.trim()) {
      return showErrorToast("Vui lòng nhập số hóa đơn.");
    }
    if (isSale && !form.branch_id) {
      return showErrorToast("Vui lòng chọn chi nhánh.");
    }
    if (!form.partner_name.trim()) {
      return showErrorToast(
        isSale ? "Vui lòng chọn khách hàng." : "Vui lòng nhập nhà cung cấp."
      );
    }

    const linePayload = formItemsToPayload(items);
    if (!linePayload.length) {
      return showErrorToast(
        "Vui lòng thêm ít nhất 1 mặt hàng (tên, SL, đơn giá)."
      );
    }

    const subtotalFromItems = sumItemsSubtotal(items);
    if (!subtotalFromItems || subtotalFromItems <= 0) {
      return showErrorToast("Tổng mặt hàng trước VAT phải lớn hơn 0.");
    }

    const total = calcTotalFromVatPercent(subtotalFromItems, form.vat_percent);
    if (!total || total <= 0) {
      return showErrorToast("Tổng tiền phải lớn hơn 0.");
    }

    const payload = buildRequestPayload({
      invoice_number: form.invoice_number.trim(),
      invoice_type: form.invoice_type,
      invoice_date: form.invoice_date,
      due_date: form.due_date || null,
      partner_name: form.partner_name.trim(),
      partner_tax_code: form.partner_tax_code?.trim() || null,
      branch_id: form.branch_id || null,
      subtotal: subtotalFromItems,
      vat_amount: calcVatAmount(subtotalFromItems, form.vat_percent),
      total_amount: total,
      processing_status: form.processing_status,
      description: form.description?.trim() || null,
      note: form.note?.trim() || null,
      items: linePayload,
    });

    try {
      setSaving(true);
      if (isEdit) {
        await updateInvoice(invoice.id, payload);
        showSuccessToast("Cập nhật hóa đơn thành công.");
      } else {
        await createInvoice(payload);
        showSuccessToast("Ghi nhận hóa đơn thành công.");
      }
      setShouldReset(true);
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Save invoice failed:", error);
      showErrorToast(
        error?.response?.data?.message || "Không thể lưu hóa đơn."
      );
    } finally {
      setSaving(false);
    }
  };

  const statusOptions = getStatusOptions(form.invoice_type);
  const selectedStatus =
    statusOptions.find((x) => x.value === form.processing_status) ||
    statusOptions[0] ||
    null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[50]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseInstant}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[51] p-4"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-6xl flex flex-col max-h-[92vh] overflow-hidden border border-slate-100">
              <div className="px-6 md:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-r from-red-50 via-white to-white">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex items-center gap-3">
                    <span className="p-2.5 bg-red-600 rounded-2xl text-white shadow-lg shadow-red-200">
                      <FileText size={20} />
                    </span>
                    <div>
                      <h2 className="text-xl font-black text-slate-800 tracking-tight">
                        {title}
                      </h2>
                      <p className="text-sm text-slate-500 mt-1">
                        Ghi nhận hóa đơn từ phần mềm khác — ERP không xuất HĐ.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseInstant}
                    className="text-slate-400 hover:text-red-600 p-2 rounded-xl hover:bg-red-50"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <StatusChip
                    icon={User}
                    label={user?.name || user?.employee_name || "Người ghi nhận"}
                  />
                  <StatusChip
                    icon={Building2}
                    label={user?.department_name || "Chưa có phòng ban"}
                  />
                  <StatusChip
                    icon={CheckCircle2}
                    label={`Hoàn thiện ${completionCount}/4 mục chính`}
                    highlighted
                  />
                </div>
              </div>

              <div className="px-6 md:px-8 py-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_360px] gap-6 items-start">
                  <div className="space-y-6">
                    <SectionCard
                      icon={FileText}
                      title="Thông tin hóa đơn"
                      description="Số HĐ, ngày và đối tượng giao dịch."
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <FieldLabel label="Chi nhánh" required={isSale} />
                          <Select
                            value={selectedBranch}
                            onChange={handleBranchChange}
                            options={branchOptions}
                            styles={branchSelectStyles}
                            menuPortalTarget={document.body}
                            isClearable={!isSale}
                            placeholder={
                              isSale ? "Chọn chi nhánh..." : "Theo người tạo"
                            }
                          />
                        </div>
                        <div>
                          <FieldLabel
                            label={isSale ? "Khách hàng" : "Nhà cung cấp"}
                            required
                          />
                          {isSale ? (
                            <>
                              <Select
                                value={selectedCustomer}
                                onChange={handleCustomerChange}
                                options={customerOptions}
                                styles={customerSelectStyles}
                                {...modalSelectPortalProps}
                                isClearable
                                isDisabled={!form.branch_id}
                                isLoading={loadingCustomers}
                                placeholder={
                                  form.branch_id
                                    ? "Chọn khách hàng..."
                                    : "Chọn chi nhánh trước..."
                                }
                                formatOptionLabel={formatCustomerOptionLabel}
                                filterOption={filterCustomerOption}
                                noOptionsMessage={() =>
                                  loadingCustomers
                                    ? "Đang tải..."
                                    : "Không có khách hàng"
                                }
                              />
                              <p className="mt-1.5 text-xs text-slate-500">
                                MST:{" "}
                                <span className="font-medium text-slate-600">
                                  {form.partner_tax_code || "—"}
                                </span>
                              </p>
                            </>
                          ) : (
                            <>
                              <input
                                value={form.partner_name}
                                onChange={(e) =>
                                  updateField("partner_name", e.target.value)
                                }
                                className="w-full h-[42px] px-4 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none"
                              />
                              {form.partner_tax_code ? (
                                <p className="mt-1.5 text-xs text-slate-500">
                                  MST:{" "}
                                  <span className="font-medium text-slate-600">
                                    {form.partner_tax_code}
                                  </span>
                                </p>
                              ) : null}
                            </>
                          )}
                        </div>
                        <div>
                          <FieldLabel label="Số hóa đơn" required />
                          <input
                            value={form.invoice_number}
                            onChange={(e) =>
                              updateField("invoice_number", e.target.value)
                            }
                            className="w-full h-[42px] px-4 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none"
                            placeholder="VD: 00001234"
                          />
                        </div>
                        <div>
                          <FieldLabel label="Ngày hóa đơn" required />
                          <div className="relative">
                            <Calendar
                              size={16}
                              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                              type="date"
                              value={form.invoice_date}
                              onChange={(e) =>
                                updateField("invoice_date", e.target.value)
                              }
                              className="w-full h-[42px] pl-11 pr-4 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <FieldLabel label="Hạn thanh toán" />
                          <div className="relative">
                            <Calendar
                              size={16}
                              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <input
                              type="date"
                              value={form.due_date}
                              onChange={(e) =>
                                updateField("due_date", e.target.value)
                              }
                              className="w-full h-[42px] pl-11 pr-4 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <FieldLabel label="Trạng thái hóa đơn" required />
                          <Select
                            value={selectedStatus}
                            onChange={(opt) =>
                              updateField(
                                "processing_status",
                                opt?.value ||
                                  (isSale ? "issued" : "pending")
                              )
                            }
                            options={statusOptions}
                            styles={branchSelectStyles}
                            menuPortalTarget={document.body}
                            placeholder="Chọn trạng thái..."
                          />
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={ListOrdered}
                      title="Mặt hàng"
                      description="Ưu tiên nhập XML HĐĐT — tự điền số HĐ, đối tác và dòng hàng."
                    >
                      <InvoiceItemsEditor
                        items={items}
                        onChange={handleItemsChange}
                        onXmlImported={handleXmlImported}
                        invoiceType={form.invoice_type}
                      />
                    </SectionCard>

                    <SectionCard
                      icon={DollarSign}
                      title="Giá trị hóa đơn"
                      description="Trước VAT = tổng mặt hàng. Tổng tiền = Trước VAT × (100% + VAT%)."
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                        <div>
                          <FieldLabel label="Trước VAT" required />
                          <input
                            readOnly
                            value={formatMoneyInput(form.subtotal)}
                            className="w-full h-[42px] px-4 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700 outline-none tabular-nums cursor-default"
                            placeholder="0"
                            title="Tự tính từ bảng mặt hàng"
                          />
                        </div>
                        <div>
                          <FieldLabel label="VAT (%)" />
                          <div className="relative">
                            <input
                              inputMode="decimal"
                              value={form.vat_percent}
                              onChange={(e) =>
                                updateMoneyField("vat_percent", e.target.value)
                              }
                              className="w-full h-[42px] px-4 pr-10 rounded-xl border border-slate-200 text-sm font-medium focus:border-red-500 outline-none tabular-nums"
                              placeholder="0"
                            />
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">
                              %
                            </span>
                          </div>
                        </div>
                        <div>
                          <FieldLabel label="Giá trị VAT" />
                          <input
                            readOnly
                            value={formatMoneyInput(
                              String(calcVatAmount(form.subtotal, form.vat_percent))
                            )}
                            className="w-full h-[42px] px-4 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700 outline-none tabular-nums cursor-default"
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <FieldLabel label="Tổng tiền" required />
                          <input
                            readOnly
                            value={formatMoneyInput(form.total_amount)}
                            className="w-full h-[42px] px-4 rounded-xl border border-red-200 bg-red-50/30 text-sm font-bold text-red-700 outline-none tabular-nums cursor-default"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </SectionCard>

                    <SectionCard
                      icon={Info}
                      title="Diễn giải & ghi chú"
                      description="Mô tả nội dung HĐ và ghi chú nội bộ."
                    >
                      <div className="space-y-4">
                        <div>
                          <FieldLabel label="Nội dung / hàng hóa dịch vụ" />
                          <textarea
                            rows={2}
                            value={form.description}
                            onChange={(e) =>
                              updateField("description", e.target.value)
                            }
                            className="w-full min-h-[72px] px-4 py-3 rounded-xl border border-slate-200 text-sm focus:border-red-500 outline-none resize-y"
                          />
                        </div>
                        <div>
                          <FieldLabel label="Ghi chú nội bộ" />
                          <textarea
                            rows={2}
                            value={form.note}
                            onChange={(e) => updateField("note", e.target.value)}
                            className="w-full min-h-[72px] px-4 py-3 rounded-xl border border-slate-200 text-sm focus:border-red-500 outline-none resize-y"
                          />
                        </div>
                      </div>
                    </SectionCard>
                  </div>

                  <div className="space-y-5">
                    <div className="rounded-[1.75rem] border border-slate-100 bg-white p-5 shadow-sm">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                          <Upload size={20} />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-slate-800">
                            Tài liệu đính kèm
                          </h3>
                          <p className="text-sm text-slate-500 mt-1">
                            PDF hoặc ảnh scan, tối đa 25MB. Ảnh sẽ tạo thumbnail lưu DB.
                          </p>
                        </div>
                      </div>

                      {attachedFile || existingAttachment ? (
                        <div
                          className={`rounded-2xl border bg-slate-50/80 p-4 transition-colors ${
                            dragOverAttach
                              ? "border-red-400 border-dashed bg-red-50/60 ring-2 ring-red-200"
                              : "border-slate-200"
                          }`}
                          onDragEnter={handleAttachDragOver}
                          onDragOver={handleAttachDragOver}
                          onDragLeave={handleAttachDragLeave}
                          onDrop={handleAttachDrop}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-red-600 shrink-0">
                              <FileText size={18} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-slate-800 truncate">
                                {previewName || existingAttachment?.name || "file.pdf"}
                              </p>
                              {attachedFile ? (
                                <div className="mt-1.5 space-y-0.5 text-[11px] text-slate-500">
                                  <p>
                                    Dung lượng:{" "}
                                    <span className="font-medium">
                                      {formatFileSize(attachedFile.size)}
                                    </span>
                                  </p>
                                  <p>
                                    Sửa lần cuối:{" "}
                                    <span className="font-medium">
                                      {formatFileDateTime(attachedFile.lastModified)}
                                    </span>
                                  </p>
                                  <p>
                                    Tải lên bởi:{" "}
                                    <span className="font-medium">
                                      {user?.name || user?.employee_name || "—"}
                                    </span>
                                  </p>
                                </div>
                              ) : existingAttachment?.url ? (
                                <a
                                  href={existingAttachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-1.5 inline-block text-xs font-medium text-red-600 hover:underline"
                                >
                                  Xem file hiện tại
                                </a>
                              ) : null}
                              {dragOverAttach ? (
                                <p className="mt-2 text-xs font-semibold text-red-600">
                                  Thả PDF để thay thế…
                                </p>
                              ) : null}
                            </div>
                            <button
                              type="button"
                              onClick={clearAttachment}
                              className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                              title="Xóa file"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <label
                            htmlFor="invoiceFileInput"
                            className="mt-3 inline-flex cursor-pointer text-xs font-semibold text-red-600 hover:underline"
                          >
                            Đổi file khác
                            <input
                              type="file"
                              id="invoiceFileInput"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                              onChange={handleFileSelect}
                            />
                          </label>
                        </div>
                      ) : (
                        <label
                          htmlFor="invoiceFileInput"
                          className={`flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${
                            dragOverAttach
                              ? "border-red-500 bg-red-50 ring-2 ring-red-200"
                              : "border-slate-200 bg-slate-50/50 hover:border-red-300 hover:bg-red-50/40"
                          }`}
                          onDragEnter={handleAttachDragOver}
                          onDragOver={handleAttachDragOver}
                          onDragLeave={handleAttachDragLeave}
                          onDrop={handleAttachDrop}
                        >
                          <div
                            className={`h-12 w-12 rounded-2xl border flex items-center justify-center shadow-sm ${
                              dragOverAttach
                                ? "bg-red-100 border-red-200 text-red-600"
                                : "bg-white border-slate-200 text-slate-400"
                            }`}
                          >
                            <Upload size={22} />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-700">
                              {dragOverAttach
                                ? "Thả PDF/ảnh vào đây"
                                : "Kéo thả hoặc chọn PDF / ảnh"}
                            </p>
                            <p className="text-xs text-slate-400 mt-1">
                              Bấm để tải lên · tối đa 25MB
                            </p>
                          </div>
                          <input
                            type="file"
                            id="invoiceFileInput"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                            onChange={handleFileSelect}
                          />
                        </label>
                      )}
                    </div>

                    <div className="rounded-[1.75rem] bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-white shadow-xl">
                      <p className="text-xs font-bold uppercase tracking-widest text-white/50">
                        Tổng giá trị
                      </p>
                      <p className="text-3xl font-black mt-2">
                        {formatCurrency(form.total_amount)} ₫
                      </p>
                      <div className="mt-5 space-y-3 border-t border-white/10 pt-4">
                        <SummaryRow
                          dark
                          label="Loại"
                          value={selectedType?.label || "—"}
                        />
                        <SummaryRow
                          dark
                          label="Trạng thái"
                          value={selectedStatus?.label || "—"}
                        />
                        <SummaryRow
                          dark
                          label="Hạn TT"
                          value={
                            form.due_date ? formatDateVN(form.due_date) : "—"
                          }
                        />
                        <SummaryRow
                          dark
                          label="Chi nhánh"
                          value={selectedBranch?.label || "Theo người tạo"}
                        />
                      </div>
                    </div>

                    <div className="rounded-[1.75rem] border border-amber-100 bg-amber-50 p-5">
                      <div className="flex items-start gap-3">
                        <Info size={18} className="text-amber-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-amber-800">Lưu ý</p>
                          <ul className="mt-2 space-y-1.5 text-xs text-amber-700">
                            <li>Chỉ ghi nhận HĐ đã xuất từ phần mềm khác.</li>
                            <li>Cập nhật trạng thái xử lý sau khi lưu.</li>
                            <li>Đính kèm PDF hoặc ảnh scan hóa đơn (ảnh → thumbnail trong DB để xem nhanh).</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 md:px-8 py-5 border-t border-slate-100 bg-slate-50/80 flex flex-col-reverse sm:flex-row justify-between gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-6 py-3 font-bold text-slate-500 hover:text-red-600 rounded-2xl"
                >
                  Hủy bỏ
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold shadow-lg shadow-red-200 disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={18} />
                  )}
                  {isEdit ? "Lưu thay đổi" : "Ghi nhận HĐ"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-[1.75rem] border border-slate-100 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">{title}</h3>
          {description ? (
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function FieldLabel({ label, required = false }) {
  return (
    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
  );
}

function SummaryRow({ label, value, dark = false }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={dark ? "text-white/60 text-xs" : "text-slate-400 text-xs"}>
        {label}
      </span>
      <span
        className={
          dark
            ? "text-right text-sm font-semibold text-white"
            : "text-right text-sm font-semibold text-slate-700"
        }
      >
        {value}
      </span>
    </div>
  );
}

function StatusChip({ icon: Icon, label, highlighted = false }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold border ${
        highlighted
          ? "bg-red-50 text-red-700 border-red-200"
          : "bg-white text-slate-600 border-slate-200"
      }`}
    >
      <Icon size={14} />
      {label}
    </span>
  );
}

export default InvoiceCreateModal;
